// utils/compressImage.js
import imageCompression from 'browser-image-compression';

/**
 * Compress a single image file
 * @param {File} file - Original image file (from <input type="file"/> or drag & drop)
 * @param {Object} customOptions - Optional compression settings
 * @returns {Promise<File>} - Compressed image file
 */
export const compressImage = async (file, customOptions = {}) => {
    if (!file) throw new Error("No file provided");

    const defaultOptions = {
        maxSizeMB: 1,           // Target maximum size in MB
        maxWidthOrHeight: 1920, // Max dimension (resize if larger)
        useWebWorker: true,     // Offload to web worker for performance
    };

    const options = { ...defaultOptions, ...customOptions };

    try {
        const compressedFile = await imageCompression(file, options);
        return compressedFile;
    } catch (err) {
        console.error("Image compression failed:", err);
        return file; // Return original if compression fails
    }
}

/**
 * Compress multiple images
 * @param {File[]} files - Array of image files
 * @param {Object} customOptions - Optional compression settings
 * @param {Function} onProgress - Optional callback(progress, index, total)
 * @returns {Promise<File[]>} - Array of compressed image files
 */
export const compressImages = async (files, customOptions = {}, onProgress) => {
    const compressedFiles = [];

    for (let i = 0; i < files.length; i++) {
        const compressed = await compressImage(files[i], customOptions);
        compressedFiles.push(compressed);

        if (onProgress) {
            const percent = Math.round(((i + 1) / files.length) * 100);
            onProgress(percent, i + 1, files.length);
        }
    }

    return compressedFiles;
};
